import {
  ADD,
  CAL,
  COL_W,
  EVAL_FIELDS,
  EXTRA_BUTTON,
  FORM,
  FORM_FIELDS,
  JOB,
  barRect,
  cellRect,
  center,
  dayNumberRect,
  grow,
  missingChipRects,
  optionRect,
  span,
  stepRect,
  todoRowRect,
  typed,
} from './stageLayout';
import { CLICKED_DAY, JOBS, TODAY, jobById, visitById } from './mockData';
import { FORM_MISSING_KEYS } from './scenes/VisitFormScene';

/*
 * The video, beat by beat. A beat is one caption (and one narration line):
 * - scene + state(p): what the stage shows, p = 0 → 1 through the beat
 * - cursor: keyframes { p, x, y, click } (positions carry over between beats)
 * - spot(p): the rectangle highlighted, or null
 * - toast(p): a notification shown on the stage
 * Durations follow the caption length so narration and reading both fit.
 */

const NEW_DAY = { row: 2, col: 3 };
// The day the new job (J3) starts on
const JOB_DAY = { row: 4, col: 0 };
const at = (rect, dx = 0, dy = 0) => {
  const point = center(rect);
  return { x: point.x + dx, y: point.y + dy };
};
const key = (p, rect, extra = {}) => ({ p, ...at(rect, extra.dx, extra.dy), click: Boolean(extra.click) });
const markPoint = (visit) => {
  const bar = barRect(visit);
  return { x: cellRect(visit.row, visit.mark).x + COL_W / 2, y: bar.y + bar.h / 2 };
};
const between = (p, from, to) => p >= from && p < to;
const pick = (p, steps) => steps.find(([until]) => p < until)?.[1] ?? steps[steps.length - 1][1];

const BEATS = (t) => {
  const C = visitById('C');
  const D = visitById('D');
  const B = visitById('B');
  const A = visitById('A');
  const N = visitById('N');
  const J1 = JOBS[0];
  const J3 = jobById('J3');
  const jobTitle = t('profilePage.tutorial.stage.jobSupplies');
  const jobCalendar = { newVisit: 'completed', selected: JOB_DAY };
  const jobBase = { calendar: jobCalendar, appear: 1 };
  // The project field with its open list (placeholder + two active projects)
  const projectMenu = { ...JOB.project, h: JOB.project.h + 8 + 3 * 34 + 8 };
  const fullForm = { visitType: 'hastane', sourceDocument: 'yes', travelType: 'local', doctor: t('profilePage.tutorial.stage.doctor'), doctorDone: true, hospital: true, city: true };
  const missingLabels = FORM_MISSING_KEYS.map((field) => t(`visitModal.fields.${field}`));
  const doctorChip = missingChipRects(missingLabels)[FORM_MISSING_KEYS.indexOf('doctor')];
  const checkbox = (index) => ({ x: todoRowRect(index).x + 24, y: todoRowRect(index).y + 22 });
  const formBase = { calendar: { newVisit: 'planned', selected: NEW_DAY }, appear: 1 };
  const addBase = { calendar: { selected: NEW_DAY }, appear: 1, date: true };

  return [
    { id: 'intro', chapter: 'intro', scene: 'title', state: () => ({ variant: 'intro' }), cursor: null, min: 9000 },

    // ── Takvime genel bakış ──
    {
      id: 'overview', chapter: 'overview', scene: 'calendar', state: () => ({}),
      cursor: [{ p: 0, x: 1150, y: 640 }, key(0.35, CAL.navTakvim), key(0.42, CAL.navTakvim, { click: true }), { p: 0.85, x: 470, y: 420 }],
      spot: (p) => (p < 0.6 ? grow(CAL.navTakvim, 5) : null),
    },
    {
      id: 'months', chapter: 'overview', scene: 'calendar', state: (p) => ({ monthShift: between(p, 0.3, 0.68) ? 1 : 0 }),
      cursor: [key(0.15, CAL.next), key(0.28, CAL.next, { click: true }), key(0.55, CAL.today), key(0.66, CAL.today, { click: true }), { p: 0.9, x: 470, y: 380 }],
      spot: (p) => pick(p, [[0.48, grow({ x: 40, y: 72, w: 268, h: 40 }, 5)], [0.75, grow(CAL.today, 5)], [1, null]]),
    },
    {
      id: 'summary', chapter: 'overview', scene: 'calendar', state: () => ({}),
      cursor: [{ p: 0.1, x: 90, y: 139 }, { p: 0.7, x: 560, y: 139 }],
      spot: () => grow(CAL.summary, 6),
    },

    // ── Takvimi okumak ──
    {
      id: 'bars', chapter: 'reading', scene: 'calendar', state: (p) => ({ highlightBar: p > 0.1 ? 'C' : null, tooltip: p > 0.45 ? 'C' : null }),
      cursor: [{ p: 0.15, ...center(barRect(C)) }, { p: 0.4, ...markPoint(C) }],
      spot: (p) => (p < 0.45 ? grow(barRect(C), 6) : null),
    },
    {
      id: 'colors', chapter: 'reading', scene: 'calendar', state: (p) => ({ legendOpen: p > 0.12 }),
      cursor: [key(0.08, CAL.help), key(0.12, CAL.help, { click: true }), { p: 0.34, ...markPoint(D) }, { p: 0.5, ...markPoint(C) }, { p: 0.65, ...markPoint(B) }, { p: 0.8, x: cellRect(A.row, A.done).x + COL_W / 2, y: barRect(A).y + 10 }],
      spot: (p) => pick(p, [[0.28, { x: 910, y: 154, w: 336, h: 210 }], [0.45, grow(barRect(D), 5)], [0.6, grow(barRect(C), 5)], [0.75, grow(barRect(B), 5)], [1, grow(barRect(A), 5)]]),
    },
    {
      id: 'stripes', chapter: 'reading', scene: 'calendar', state: (p) => ({ highlightBar: p < 0.5 ? 'D' : 'J1' }),
      cursor: [{ p: 0.15, x: barRect(D).x + 2, y: barRect(D).y + 10 }, { p: 0.55, ...center(barRect(J1)) }],
      spot: (p) => (p < 0.5 ? { x: barRect(D).x - 7, y: barRect(D).y - 7, w: 26, h: 35 } : grow(barRect(J1), 6)),
    },
    {
      id: 'declutter', chapter: 'reading', scene: 'calendar',
      state: (p) => ({ showWindows: !between(p, 0.2, 0.55), hideCompleted: between(p, 0.7, 0.88) }),
      cursor: [key(0.1, CAL.windows), key(0.2, CAL.windows, { click: true }), key(0.55, CAL.windows, { click: true }), key(0.65, CAL.hideCompleted), key(0.7, CAL.hideCompleted, { click: true }), key(0.88, CAL.hideCompleted, { click: true }), key(0.96, CAL.projects)],
      spot: (p) => pick(p, [[0.6, grow(CAL.windows, 5)], [0.9, grow(CAL.hideCompleted, 5)], [1, grow(CAL.projects, 5)]]),
    },

    // ── Bir günün planı ──
    {
      id: 'day', chapter: 'day', scene: 'calendar', state: (p) => ({ selected: p > 0.35 ? CLICKED_DAY : TODAY }),
      cursor: [key(0.15, dayNumberRect(CLICKED_DAY.row, CLICKED_DAY.col)), key(0.35, dayNumberRect(CLICKED_DAY.row, CLICKED_DAY.col), { click: true }), { p: 0.75, x: 1076, y: 260 }],
      spot: (p) => (p < 0.35 ? cellRect(CLICKED_DAY.row, CLICKED_DAY.col) : grow(CAL.panel, 4)),
    },
    {
      id: 'dayLists', chapter: 'day', scene: 'calendar', state: (p) => ({ selected: CLICKED_DAY, panelHighlight: p < 0.45 ? 'planned' : 'windows' }),
      cursor: [{ p: 0.1, x: 1010, y: 250 }, { p: 0.5, x: 1010, y: 420 }],
      spot: () => null,
    },

    // ── Yeni vizit açmak ──
    {
      id: 'addOpen', chapter: 'addVisit', scene: 'addVisit',
      state: (p) => ({ ...addBase, calendar: { selected: p > 0.15 ? NEW_DAY : CLICKED_DAY }, appear: span(p, 0.45, 0.6) }),
      cursor: [key(0.05, dayNumberRect(NEW_DAY.row, NEW_DAY.col)), key(0.15, dayNumberRect(NEW_DAY.row, NEW_DAY.col), { click: true }), key(0.32, CAL.addVisit), key(0.4, CAL.addVisit, { click: true }), key(0.8, ADD.project, { dx: 60 })],
      spot: (p) => pick(p, [[0.2, cellRect(NEW_DAY.row, NEW_DAY.col)], [0.45, grow(CAL.addVisit, 5)], [1, null]]),
    },
    {
      id: 'addWho', chapter: 'addVisit', scene: 'addVisit',
      state: (p) => ({
        ...addBase,
        focus: pick(p, [[0.3, 'project'], [0.68, 'patient'], [1, p > 0.76 ? 'addPatient' : null]]),
        menu: between(p, 0.08, 0.28) ? 'project' : between(p, 0.45, 0.65) ? 'patient' : null,
        menuHover: between(p, 0.08, 0.28) ? (p > 0.18 ? 0 : -1) : (p > 0.55 ? 1 : -1),
        project: p >= 0.28 ? 'onko' : '',
        patient: p >= 0.65 ? 'P-1120' : '',
      }),
      cursor: [key(0, ADD.project, { dx: 60 }), key(0.08, ADD.project, { dx: 60, click: true }), key(0.2, optionRect(ADD.project, 0), { dx: -60 }), key(0.28, optionRect(ADD.project, 0), { dx: -60, click: true }), key(0.4, ADD.patient), key(0.45, ADD.patient, { click: true }), key(0.56, optionRect(ADD.patient, 1), { dx: -40 }), key(0.65, optionRect(ADD.patient, 1), { dx: -40, click: true }), key(0.8, ADD.addPatient)],
      spot: (p) => pick(p, [[0.32, grow(ADD.project, 5)], [0.7, grow(ADD.patient, 5)], [1, grow(ADD.addPatient, 5)]]),
    },
    {
      id: 'addWhen', chapter: 'addVisit', scene: 'addVisit',
      state: (p) => ({ ...addBase, project: 'onko', patient: 'P-1120', focus: between(p, 0.05, 0.2) ? 'date' : between(p, 0.2, 0.45) ? 'quick3' : null, windowSet: p >= 0.32 }),
      cursor: [key(0.12, ADD.date), key(0.3, ADD.quick3), key(0.32, ADD.quick3, { click: true }), key(0.65, ADD.summary, { dx: -200 })],
      spot: (p) => pick(p, [[0.22, grow(ADD.date, 5)], [0.45, grow(ADD.quick, 5)], [1, grow(ADD.summary, 5)]]),
    },
    {
      id: 'addDetails', chapter: 'addVisit', scene: 'addVisit',
      state: (p) => ({
        ...addBase,
        project: 'onko',
        patient: 'P-1120',
        windowSet: true,
        focus: p < 0.4 ? 'type' : 'number',
        menu: between(p, 0.12, 0.34) ? 'type' : null,
        menuHover: p > 0.24 ? 0 : -1,
        type: p >= 0.34 ? 'hastane' : '',
        number: p >= 0.45 ? '4' : '',
      }),
      cursor: [key(0.08, ADD.type, { dx: 60 }), key(0.12, ADD.type, { dx: 60, click: true }), key(0.25, optionRect(ADD.type, 0), { dx: -80 }), key(0.33, optionRect(ADD.type, 0), { dx: -80, click: true }), key(0.52, ADD.number)],
      spot: (p) => (p < 0.4 ? grow(ADD.type, 5) : grow({ x: 644, y: 474, w: 368, h: 76 }, 5)),
    },
    {
      id: 'addSave', chapter: 'addVisit', scene: 'addVisit',
      state: (p) => ({
        ...addBase,
        calendar: { selected: NEW_DAY, newVisit: p >= 0.72 ? 'planned' : 'hidden', highlightBar: p >= 0.75 ? 'N' : null },
        appear: p < 0.72 ? 1 : 1 - span(p, 0.72, 0.8),
        project: 'onko',
        patient: 'P-1120',
        windowSet: true,
        type: 'hastane',
        number: '4',
        repeat: between(p, 0.15, 0.4),
        saving: between(p, 0.6, 0.72),
      }),
      cursor: [key(0.1, ADD.repeat, { dx: 150 }), key(0.15, ADD.repeat, { dx: 150, click: true }), key(0.4, ADD.repeat, { dx: 150, click: true }), key(0.55, ADD.save), key(0.6, ADD.save, { click: true }), { p: 0.9, ...markPoint(N) }],
      spot: (p) => pick(p, [[0.45, grow(ADD.repeat, 5)], [0.72, grow(ADD.save, 5)], [1, grow(barRect(N), 6)]]),
      toast: (p) => (between(p, 0.74, 0.99) ? { text: t('profilePage.calendar.visitForm.saved_one') } : null),
    },

    // ── Vizit formunu doldurmak ──
    {
      id: 'formOpen', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({ ...formBase, appear: span(p, 0.45, 0.6), step: 1 }),
      cursor: [{ p: 0.1, ...markPoint(N) }, { p: 0.35, ...markPoint(N), click: true }, { p: 0.8, x: 200, y: 160 }],
      spot: (p) => (p < 0.42 ? grow(barRect(N), 6) : null),
    },
    {
      id: 'formRail', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({ ...formBase, step: 1, railFocus: p < 0.5 ? 'progress' : null }),
      cursor: [key(0.1, FORM.progress), key(0.55, stepRect(1)), key(0.7, stepRect(2)), key(0.85, stepRect(6))],
      spot: (p) => (p < 0.5 ? grow(FORM.progress, 5) : { x: 46, y: 212, w: 244, h: 7 * 52 + 4 }),
    },
    {
      id: 'formTodo', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({ ...formBase, step: p >= 0.1 ? 2 : 1, todo: p >= 0.65 ? 3 : p >= 0.5 ? 2 : p >= 0.35 ? 1 : 0 }),
      cursor: [key(0.06, stepRect(1)), key(0.1, stepRect(1), { click: true }), { p: 0.3, ...checkbox(0) }, { p: 0.35, ...checkbox(0), click: true }, { p: 0.45, ...checkbox(1) }, { p: 0.5, ...checkbox(1), click: true }, { p: 0.6, ...checkbox(2) }, { p: 0.65, ...checkbox(2), click: true }, { p: 0.85, x: 600, y: 230 }],
      spot: (p) => (p < 0.12 ? grow(stepRect(1), 4) : { x: 314, y: 290, w: 908, h: 226 }),
    },
    {
      id: 'formMissing', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({ ...formBase, todo: 3, step: p >= 0.08 ? 3 : 2, chipFocus: between(p, 0.5, 0.7) ? 'doctor' : null, fieldFocus: p >= 0.6 ? 'doctor' : null }),
      cursor: [key(0.04, FORM.next), key(0.08, FORM.next, { click: true }), { p: 0.3, x: 470, y: 217 }, key(0.5, doctorChip), key(0.55, doctorChip, { click: true }), key(0.78, FORM_FIELDS.doctor)],
      spot: (p) => pick(p, [[0.12, grow(FORM.next, 5)], [0.6, grow(FORM.missing, 4)], [1, grow(FORM_FIELDS.doctor, 5)]]),
    },
    {
      id: 'formFields', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({
        ...formBase,
        todo: 3,
        step: 3,
        form: {
          visitType: p >= 0.1 ? 'hastane' : '',
          sourceDocument: p >= 0.2 ? 'yes' : '',
          travelType: p >= 0.3 ? 'local' : '',
          doctor: typed(fullForm.doctor, p, 0.42, 0.55),
          doctorDone: p >= 0.56,
          hospital: p >= 0.7,
          city: p >= 0.75,
        },
        fieldFocus: pick(p, [[0.4, null], [0.6, 'doctor'], [0.73, 'hospital'], [0.88, 'city'], [1, null]]),
      }),
      cursor: [key(0.07, FORM_FIELDS.types[0]), key(0.1, FORM_FIELDS.types[0], { click: true }), key(0.17, FORM_FIELDS.sourceYes), key(0.2, FORM_FIELDS.sourceYes, { click: true }), key(0.27, FORM_FIELDS.travelLocal), key(0.3, FORM_FIELDS.travelLocal, { click: true }), key(0.37, FORM_FIELDS.doctor), key(0.4, FORM_FIELDS.doctor, { click: true }), key(0.6, FORM_FIELDS.hospital), key(0.65, FORM_FIELDS.hospital, { click: true }), key(0.82, FORM_FIELDS.city)],
      spot: (p) => pick(p, [[0.35, grow(FORM_FIELDS.card1, 3)], [0.6, grow(FORM_FIELDS.doctor, 5)], [0.72, grow(FORM_FIELDS.hospital, 5)], [1, grow(FORM_FIELDS.city, 5)]]),
    },
    {
      id: 'formExtras', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({ ...formBase, todo: 3, form: fullForm, step: pick(p, [[0.1, 3], [0.4, 4], [0.7, 5], [1, 6]]) }),
      cursor: [key(0.06, stepRect(3)), key(0.1, stepRect(3), { click: true }), key(0.25, EXTRA_BUTTON), key(0.36, stepRect(4)), key(0.4, stepRect(4), { click: true }), key(0.55, EXTRA_BUTTON), key(0.66, stepRect(5)), key(0.7, stepRect(5), { click: true }), key(0.85, EXTRA_BUTTON)],
      spot: (p) => (between(p, 0.15, 0.33) || between(p, 0.45, 0.63) || p > 0.75 ? grow(EXTRA_BUTTON, 5) : null),
    },
    {
      id: 'formEval', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({
        ...formBase,
        todo: 3,
        form: fullForm,
        step: p >= 0.06 ? 7 : 6,
        answers: {
          screenFail: p >= 0.2 ? 'no' : '',
          dropPatient: p >= 0.32 ? 'no' : '',
          protocolDeviation: p >= 0.44 ? 'no' : '',
          processEndDate: p >= 0.58,
          slaTarget: p >= 0.72,
          issueOccurred: p >= 0.84 ? 'no' : '',
        },
        fieldFocus: between(p, 0.54, 0.62) ? 'processEndDate' : between(p, 0.68, 0.76) ? 'slaTarget' : null,
        railFocus: p > 0.88 ? 'progress' : null,
      }),
      cursor: [key(0.03, stepRect(6)), key(0.06, stepRect(6), { click: true }), key(0.16, EVAL_FIELDS.screenFail.no), key(0.2, EVAL_FIELDS.screenFail.no, { click: true }), key(0.28, EVAL_FIELDS.dropPatient.no), key(0.32, EVAL_FIELDS.dropPatient.no, { click: true }), key(0.4, EVAL_FIELDS.protocolDeviation.no), key(0.44, EVAL_FIELDS.protocolDeviation.no, { click: true }), key(0.52, EVAL_FIELDS.processEndDate), key(0.56, EVAL_FIELDS.processEndDate, { click: true }), key(0.66, EVAL_FIELDS.slaTarget), key(0.7, EVAL_FIELDS.slaTarget, { click: true }), key(0.8, EVAL_FIELDS.issueOccurred.no), key(0.84, EVAL_FIELDS.issueOccurred.no, { click: true }), key(0.95, FORM.progress)],
      spot: (p) => (p > 0.88 ? grow(FORM.progress, 5) : null),
    },
    {
      id: 'formComplete', chapter: 'visitForm', scene: 'visitForm',
      state: (p) => ({
        ...formBase,
        todo: 3,
        form: fullForm,
        step: 7,
        answers: { screenFail: 'no', dropPatient: 'no', protocolDeviation: 'no', processEndDate: true, slaTarget: true, issueOccurred: 'no' },
        completing: between(p, 0.6, 0.74),
      }),
      cursor: [key(0.2, FORM.save), key(0.5, FORM.complete), key(0.6, FORM.complete, { click: true })],
      spot: (p) => (p < 0.45 ? grow(FORM.save, 5) : grow(FORM.complete, 5)),
      toast: (p) => (p >= 0.75 ? { text: t('profilePage.tutorial.stage.toastCompleted') } : null),
    },
    {
      id: 'formDone', chapter: 'visitForm', scene: 'calendar', state: () => ({ newVisit: 'completed', highlightBar: 'N', selected: NEW_DAY }),
      cursor: [{ p: 0.2, ...markPoint(N) }],
      spot: () => grow(barRect(N), 6),
    },

    // ── Kendi işinizi eklemek ──
    {
      id: 'jobOpen', chapter: 'addJob', scene: 'addJob',
      state: (p) => ({ calendar: { ...jobCalendar, selected: p > 0.15 ? JOB_DAY : NEW_DAY }, appear: span(p, 0.45, 0.6) }),
      cursor: [key(0.05, dayNumberRect(JOB_DAY.row, JOB_DAY.col)), key(0.15, dayNumberRect(JOB_DAY.row, JOB_DAY.col), { click: true }), key(0.32, CAL.addJob), key(0.4, CAL.addJob, { click: true }), key(0.8, JOB.start)],
      spot: (p) => pick(p, [[0.2, cellRect(JOB_DAY.row, JOB_DAY.col)], [0.45, grow(CAL.addJob, 5)], [0.62, null], [1, grow({ x: JOB.start.x, y: JOB.start.y, w: JOB.end.x + JOB.end.w - JOB.start.x, h: JOB.start.h }, 5)]]),
    },
    {
      id: 'jobDetails', chapter: 'addJob', scene: 'addJob',
      state: (p) => ({
        ...jobBase,
        title: typed(jobTitle, p, 0.1, 0.4),
        focus: pick(p, [[0.45, 'title'], [0.78, 'end'], [1, 'time']]),
        endShift: p >= 0.62 ? 1 : 0,
      }),
      cursor: [key(0, JOB.title), key(0.06, JOB.title, { click: true }), key(0.5, JOB.end), key(0.56, JOB.end, { click: true }), key(0.88, JOB.time)],
      spot: (p) => pick(p, [[0.45, grow(JOB.title, 5)], [0.78, grow(JOB.end, 5)], [1, grow(JOB.time, 5)]]),
    },
    {
      id: 'jobProject', chapter: 'addJob', scene: 'addJob',
      state: (p) => ({
        ...jobBase,
        title: jobTitle,
        endShift: 1,
        focus: 'project',
        menu: between(p, 0.14, 0.5) ? 'project' : null,
        menuHover: p > 0.3 ? 1 : -1,
        project: p >= 0.5 ? 'onko' : '',
      }),
      cursor: [key(0.08, JOB.project, { dx: 160 }), key(0.14, JOB.project, { dx: 160, click: true }), key(0.32, optionRect(JOB.project, 1), { dx: -120 }), key(0.5, optionRect(JOB.project, 1), { dx: -120, click: true }), key(0.75, JOB.project, { dx: 160, dy: 40 })],
      spot: (p) => (p < 0.5 ? grow(projectMenu, 5) : grow({ ...JOB.project, h: JOB.project.h + 44 }, 5)),
    },
    {
      id: 'jobSave', chapter: 'addJob', scene: 'addJob',
      state: (p) => ({
        ...jobBase,
        calendar: { ...jobCalendar, newJob: p >= 0.4, highlightBar: p >= 0.44 ? 'J3' : null },
        appear: p < 0.4 ? 1 : 1 - span(p, 0.4, 0.48),
        title: jobTitle,
        endShift: 1,
        project: 'onko',
        saving: between(p, 0.22, 0.4),
      }),
      cursor: [key(0.12, JOB.save), key(0.22, JOB.save, { click: true }), { p: 0.55, x: barRect(J3).x + 3, y: barRect(J3).y + 10 }, { p: 0.82, x: 1180, y: 318 }],
      spot: (p) => pick(p, [[0.4, grow(JOB.save, 5)], [0.75, { x: barRect(J3).x - 7, y: barRect(J3).y - 7, w: barRect(J3).w + 14, h: 35 }], [1, grow({ x: 928, y: 238, w: 296, h: 68 }, 4)]]),
      toast: (p) => (between(p, 0.44, 0.99) ? { text: t('profilePage.calendar.jobForm.added') } : null),
    },

    // ── Unutmayın ──
    { id: 'tipsDeadline', chapter: 'tips', scene: 'title', state: () => ({ variant: 'deadline' }), cursor: null, min: 9000 },
    { id: 'tipsEnd', chapter: 'tips', scene: 'title', state: () => ({ variant: 'end' }), cursor: null, min: 8000 },
  ];
};

export const CHAPTER_KEYS = ['intro', 'overview', 'reading', 'day', 'addVisit', 'visitForm', 'addJob', 'tips'];

/** Beats with start/duration for the current language, the chapters and the total length. */
export const buildTimeline = (t) => {
  let start = 0;
  const beats = BEATS(t).map((beat) => {
    const caption = t(`profilePage.tutorial.beats.${beat.id}`);
    const duration = Math.max(beat.min || 0, caption.length * 68 + 1800);
    const entry = { ...beat, caption, start, duration };
    start += duration;
    return entry;
  });
  const chapters = CHAPTER_KEYS.map((chapter) => {
    const inChapter = beats.filter((beat) => beat.chapter === chapter);
    const first = inChapter[0];
    const last = inChapter[inChapter.length - 1];
    return { key: chapter, start: first.start, end: last.start + last.duration };
  });
  return { beats, chapters, total: start };
};

export const beatIndexAt = (beats, time) => {
  for (let index = beats.length - 1; index >= 0; index -= 1) {
    if (time >= beats[index].start) return index;
  }
  return 0;
};
